/**
 * batch-61 portal-collega (FO-446) - DE pure regels achter "sta ik alleen of
 * met een collega van STREEP?" op een dienst-kaart in de portal.
 *
 * DB-vrij en tijdzone-vrij: alle tijden zijn `HH:mm`-wandkloktijden op DEZELFDE
 * kalenderdag (de dagregel-conventie), zodat de API, de portal en de tests
 * identiek rekenen. Gedeeld door:
 *
 *   - de server-side afleiding (`services/collega-op-dienst.ts`): welke
 *     dagregel bij dezelfde klant is een collega (SAMEN / AFLOSSING / GEEN);
 *   - de wijzigingsmelding (`services/collega-bezetting-melding.ts`): wie moet
 *     horen dat de bezetting van zijn dienst wijzigt;
 *   - de portal-UI: de kaart-regel, de tijd-hints ("Rico tot 15:00") en de
 *     week-badge - per constructie dezelfde lezing als de kaarten (T9).
 *
 * Regels (spec §3.A):
 *   - "samen" = zelfde klant/locatie én tijdoverlap >= drempel (default 60 min);
 *   - overlap kleiner dan de drempel maar de collega begint rond het einde van
 *     de eigen dienst = aflossing ("Aflossing door Tim om 15:00");
 *   - per dienst, niet per dag: een ochtend-collega telt niet voor een
 *     middagdienst.
 */

import {
  COLLEGA_OVERLAP_DREMPEL_MINUTEN_DEFAULT,
  type PlanningCollegaDTO,
} from '@streep/types'

const DAG_MINUTEN = 24 * 60

export interface CollegaDienstTijden {
  /** `HH:mm`; leeg/null = onbekend (telt als begin van de dag). */
  start: string | null | undefined
  /** `HH:mm`; leeg/null = onbekend (telt als einde van de dag). */
  eind: string | null | undefined
}

function hhmmNaarMinuten(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const uur = Number(m[1])
  const minuut = Number(m[2])
  if (uur > 24 || minuut > 59) return null
  return uur * 60 + minuut
}

function minutenNaarHhmm(minuten: number): string {
  const m = ((minuten % DAG_MINUTEN) + DAG_MINUTEN) % DAG_MINUTEN
  const uur = Math.floor(m / 60)
  const rest = m % 60
  return `${String(uur).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

/**
 * Normaliseert een dienst naar minuten sinds middernacht van de kalenderdag.
 * Een eind op of vóór de start leest als "over middernacht" (nachtdienst
 * 22:00-06:00 -> 1320..1800), net als `tijdvakkenOverlappen` in de
 * bezettingsstatus-resolver. Ontbrekende of onleesbare tijden tellen als de
 * hele dag: liever een onterecht "samen" dan een gemiste collega.
 */
export function dienstNaarMinuten(d: CollegaDienstTijden): { start: number; eind: number } {
  const start = hhmmNaarMinuten(d.start) ?? 0
  const eindRuw = hhmmNaarMinuten(d.eind)
  let eind = eindRuw ?? DAG_MINUTEN
  if (eindRuw !== null && eind <= start) eind += DAG_MINUTEN
  return { start, eind }
}

/** Overlap in minuten tussen twee diensten op dezelfde kalenderdag (0 = geen). */
export function overlapMinuten(a: CollegaDienstTijden, b: CollegaDienstTijden): number {
  const x = dienstNaarMinuten(a)
  const y = dienstNaarMinuten(b)
  return Math.max(0, Math.min(x.eind, y.eind) - Math.max(x.start, y.start))
}

export type CollegaRelatie =
  | { soort: 'SAMEN'; overlapMinuten: number }
  | { soort: 'AFLOSSING'; om: string }
  | { soort: 'GEEN' }

/**
 * De relatie van een andere dagregel (zelfde klant/locatie, zelfde dag) tot de
 * eigen dienst.
 *
 *   SAMEN      overlap >= drempel (en > 0);
 *   AFLOSSING  geen (voldoende) overlap, maar de ander begint binnen `drempel`
 *              minuten vóór of ná het einde van de eigen dienst - de
 *              aansluitende ploegendienst (07:00-15:30 gevolgd door 15:00-23:00
 *              of 15:30-23:30);
 *   GEEN       alles anders (een ochtend-collega bij een middagdienst, of een
 *              voorganger die eindigt waar de eigen dienst begint - die staat
 *              er niet "samen met" jou).
 */
export function bepaalCollegaRelatie(
  eigen: CollegaDienstTijden,
  ander: CollegaDienstTijden,
  drempelMinuten: number = COLLEGA_OVERLAP_DREMPEL_MINUTEN_DEFAULT,
  /**
   * Kalenderdag van `ander` t.o.v. `eigen` (-1, 0 of 1). Een dagregel is
   * gedateerd op de dag waarop de dienst begint; de aflosser van een
   * nachtdienst (22:00-06:00) staat daardoor op de VOLGENDE dag (06:00-14:00)
   * en een nachtcollega van gisteren kan nog in je vroege dienst van vandaag
   * lopen. Met `dagVerschil` rekent de regel op absolute minuten.
   */
  dagVerschil: -1 | 0 | 1 = 0,
): CollegaRelatie {
  const drempel = Math.max(1, Math.floor(drempelMinuten))
  const e = dienstNaarMinuten(eigen)
  const aRuw = dienstNaarMinuten(ander)
  const a = { start: aRuw.start + dagVerschil * DAG_MINUTEN, eind: aRuw.eind + dagVerschil * DAG_MINUTEN }
  const overlap = Math.max(0, Math.min(e.eind, a.eind) - Math.max(e.start, a.start))
  if (overlap > 0 && overlap >= drempel) return { soort: 'SAMEN', overlapMinuten: overlap }

  // Een dienst zonder starttijd kan niemand "aflossen" (start = 0 is dan een
  // aanname, geen tijd).
  if (hhmmNaarMinuten(ander.start) === null) return { soort: 'GEEN' }
  if (a.start >= e.eind - drempel && a.start <= e.eind + drempel) {
    return { soort: 'AFLOSSING', om: minutenNaarHhmm(aRuw.start) }
  }
  return { soort: 'GEEN' }
}

/**
 * Tijd-hint bij een collega die deels overlapt: "tot 15:00" (gaat eerder weg),
 * "vanaf 13:00" (komt later) of "13:00 - 15:00" (zit binnen je dienst). `null`
 * wanneer de collega je hele dienst dekt of de tijden ontbreken.
 */
export function collegaTijdHint(
  eigen: CollegaDienstTijden,
  collega: { van: string | null; tot: string | null },
): string | null {
  if (!collega.van && !collega.tot) return null
  const e = dienstNaarMinuten(eigen)
  const c = dienstNaarMinuten({ start: collega.van, eind: collega.tot })
  const laterBegin = collega.van != null && c.start > e.start
  const eerderEind = collega.tot != null && c.eind < e.eind
  if (laterBegin && eerderEind) return `${collega.van} - ${collega.tot}`
  if (laterBegin) return `vanaf ${collega.van}`
  if (eerderEind) return `tot ${collega.tot}`
  return null
}

/** Avatar-initialen ("Rico de Vries" -> "RV"); leeg bij een onbekende naam. */
export function initialen(naam: string | null | undefined): string {
  if (!naam) return ''
  const delen = naam.trim().split(/\s+/).filter(Boolean)
  if (delen.length === 0) return ''
  const eerste = delen[0]!.charAt(0)
  const laatste = delen.length > 1 ? delen[delen.length - 1]!.charAt(0) : ''
  return `${eerste}${laatste}`.toUpperCase()
}

/** "Rico", "Rico en Tim", "Rico, Tim en Jeroen". */
export function namenLijst(namen: ReadonlyArray<string>): string {
  if (namen.length === 0) return ''
  if (namen.length === 1) return namen[0]!
  return `${namen.slice(0, -1).join(', ')} en ${namen[namen.length - 1]!}`
}

/** Label voor een collega zonder naam (besloten werkbon). */
export const COLLEGA_VAN_STREEP_LABEL = 'een collega van STREEP'
/** Idem, als kop (sheet). */
export const COLLEGA_VAN_STREEP_KOP = 'Collega van STREEP'

function weergaveNaam(c: PlanningCollegaDTO): string {
  return c.voornaam ?? c.naam ?? COLLEGA_VAN_STREEP_LABEL
}

/** "Rico en Tim" / "Rico en een collega van STREEP" / "Rico en 2 collega's van STREEP". */
function samenNamen(samen: ReadonlyArray<PlanningCollegaDTO>): string {
  const metNaam = samen.filter((c) => c.status !== 'BESLOTEN').map(weergaveNaam)
  const besloten = samen.filter((c) => c.status === 'BESLOTEN').length
  const namen = [...metNaam]
  if (besloten === 1) namen.push(COLLEGA_VAN_STREEP_LABEL)
  else if (besloten > 1) namen.push(`${besloten} collega's van STREEP`)
  return namenLijst(namen)
}

export type CollegaRegelSoort = 'ALLEEN' | 'SAMEN' | 'AFWEZIG' | 'ONBEMAND'

export interface CollegaRegel {
  soort: CollegaRegelSoort
  /** Hoofdregel op de kaart. */
  tekst: string
  /** Kleine subregels: tijd-hints, afwezige collega's, "nog niet ingepland". */
  details: string[]
  /** "Aflossing door Tim om 15:00" (apart onder de kaart), anders null. */
  aflossing: string | null
  /** Collega's die als "samen" tellen (SAMEN + BESLOTEN) - voor avatars en badge. */
  aantalSamen: number
}

/**
 * De kaart-regel uit de DTO-lijst. `eigen` (de eigen dienst-tijden) voedt de
 * tijd-hints; zonder `eigen` blijven die achterwege.
 */
export function bouwCollegaRegel(
  collegas: ReadonlyArray<PlanningCollegaDTO> | null | undefined,
  eigen?: CollegaDienstTijden,
): CollegaRegel {
  const lijst = collegas ?? []
  const samen = lijst.filter((c) => c.status === 'SAMEN' || c.status === 'BESLOTEN')
  const afwezig = lijst.filter((c) => c.status === 'AFWEZIG')
  const onbemand = lijst.filter((c) => c.status === 'ONBEMAND')
  const aflossers = lijst.filter((c) => c.status === 'AFLOSSING')

  const details: string[] = []
  if (eigen) {
    for (const c of samen) {
      if (c.status !== 'SAMEN') continue
      const hint = collegaTijdHint(eigen, { van: c.van, tot: c.tot })
      if (hint) details.push(`${weergaveNaam(c)} ${hint}`)
    }
  }
  const afwezigTekst = (c: PlanningCollegaDTO): string =>
    `${weergaveNaam(c)} (afwezig) - vervanging wordt ingepland`

  const aflossing =
    aflossers.length > 0
      ? aflossers
          .map((c) => `Aflossing door ${weergaveNaam(c)}${c.van ? ` om ${c.van}` : ''}`)
          .join(' · ')
      : null

  if (samen.length > 0) {
    for (const c of afwezig) details.push(afwezigTekst(c))
    if (onbemand.length > 0) details.push('Collega nog niet ingepland')
    return {
      soort: 'SAMEN',
      tekst: `Samen met ${samenNamen(samen)}`,
      details,
      aflossing,
      aantalSamen: samen.length,
    }
  }
  if (afwezig.length > 0) {
    if (onbemand.length > 0) details.push('Collega nog niet ingepland')
    return {
      soort: 'AFWEZIG',
      tekst: afwezig.map(afwezigTekst).join(' · '),
      details,
      aflossing,
      aantalSamen: 0,
    }
  }
  if (onbemand.length > 0) {
    return { soort: 'ONBEMAND', tekst: 'Collega nog niet ingepland', details, aflossing, aantalSamen: 0 }
  }
  return { soort: 'ALLEEN', tekst: 'Je staat alleen', details, aflossing, aantalSamen: 0 }
}

export interface WeekDagCollegaBadge {
  soort: 'ALLEEN' | 'SAMEN'
  /** Aantal verschillende collega's op die dag (0 bij ALLEEN). */
  aantal: number
}

/**
 * De week-badge per dag, afgeleid uit DEZELFDE kaart-lijsten (T9: badges ==
 * kaarten). Kaarten zonder collega-lezing (standby-slot, kantoorbon: `null`)
 * tellen niet mee; een dag met alleen zulke kaarten krijgt geen badge.
 */
export function weekDagCollegaBadge(
  kaarten: ReadonlyArray<ReadonlyArray<PlanningCollegaDTO> | null | undefined>,
): WeekDagCollegaBadge | null {
  const relevant = kaarten.filter(
    (k): k is ReadonlyArray<PlanningCollegaDTO> => k !== null && k !== undefined,
  )
  if (relevant.length === 0) return null
  const namen = new Set<string>()
  let besloten = 0
  for (const kaart of relevant) {
    for (const c of kaart) {
      if (c.status === 'SAMEN') namen.add(c.naam ?? c.voornaam ?? '?')
      else if (c.status === 'BESLOTEN') besloten += 1
    }
  }
  const aantal = namen.size + besloten
  return aantal > 0 ? { soort: 'SAMEN', aantal } : { soort: 'ALLEEN', aantal: 0 }
}

// ─── Wijzigingsmelding (spec §3.C) ───────────────────────────────────────────

/**
 * Herkent de datum-notaties die de dagregel-mutatie-events meegeven
 * (`yyyy-mm-dd` uit het planbord, `dd-mm-yyyy` uit de nl-NL-formatter van de
 * werkbon-routes) en normaliseert naar `yyyy-mm-dd`. Onbekend -> `null`.
 */
export function normaliseerDatumNaarYmd(waarde: string | number | null | undefined): string | null {
  if (waarde === null || waarde === undefined) return null
  const s = String(waarde).trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const nl = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s)
  if (nl) return `${nl[3]}-${nl[2]}-${nl[1]}`
  return null
}

/**
 * "vandaag" / "morgen" / "op ma 08-09" voor de wijzigingsmelding. Beide
 * datums zijn NL-kalenderdagen (`yyyy-mm-dd`).
 */
export function dagLabelRelatief(ymd: string, vandaagYmd: string): string {
  if (ymd === vandaagYmd) return 'vandaag'
  const morgen = new Date(`${vandaagYmd}T00:00:00.000Z`)
  morgen.setUTCDate(morgen.getUTCDate() + 1)
  if (ymd === morgen.toISOString().slice(0, 10)) return 'morgen'
  const d = new Date(`${ymd}T12:00:00.000Z`)
  if (Number.isNaN(d.getTime())) return `op ${ymd}`
  const weekdag = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', timeZone: 'UTC' }).format(d)
  const dagMaand = new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(d)
  return `op ${weekdag} ${dagMaand}`
}
